<script lang="ts">
import { defineComponent, computed, watch, PropType } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import CharacterCount from '@tiptap/extension-character-count';

export default defineComponent({
  name: 'Editor',
  components: {
    EditorContent,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    characterLimit: {
      type: Number,
      required: false,
      default: 1000,
    },
    activeButtons: {
      type: Array as PropType<string[]>,
      validator: function (list: string[]) {
        for (let el of list) {
          if (
              [
                'bold',
                'italic',
                'strike',
                'underline',
                'h1',
                'h2',
                'h3',
                'bulletList',
                'orderedList',
                'blockquote',
                'codeBlock',
                'horizontalRule',
                'undo',
                'redo',
              ].indexOf(el) === -1
          ) {
            return false;
          }
        }
        return true;
      },
      default: () => [
        'bold',
        'italic',
        'strike',
        'underline',
        'bulletList',
        'orderedList',
        'undo',
        'redo',
      ],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const editor = new Editor({
      content: props.modelValue,
      extensions: [
        StarterKit.configure({
          blockquote: false,
          code: false,
          codeBlock: false,
          dropcursor: false,
          gapcursor: false,
          hardBreak: false,
          heading: false,
          horizontalRule: false,
        }),
        Underline, CharacterCount.configure({ limit: props.characterLimit })
      ],
      onUpdate: () => {
        emit('update:modelValue', editor.getHTML());
      },
    });

    const percentage = computed(() => {
      return Math.round((100 / props.characterLimit) * (editor.storage.characterCount.characters() || 0));
    });

    const strokeColor = computed(() => {
      const red = Math.min(255, Math.floor((255 * (percentage.value - 50)) / 50));
      const green = Math.min(255, Math.floor((255 * (100 - percentage.value)) / 50));
      return `rgb(${Math.max(0, red)},${Math.max(0, green)},0)`;
    });

    watch(() => props.modelValue, (value) => {
      if (editor.getHTML() === value) {
        return;
      }
      editor.commands.setContent(value, false);
    });

    return {
      editor,
      percentage,
      strokeColor,
    };
  },
  beforeUnmount() {
    this.editor?.destroy();
  },
});
</script>

<template>
  <div class="editor">
    <div class="menubar">
      <span v-for="actionName in activeButtons" :key="actionName">
        <button tabindex="-1"
            aria-label="Bold"
            v-if="actionName === 'bold'"
            :class="{ 'is-active': editor.isActive('bold') }"
            @click.prevent="editor.chain().focus().toggleBold().run()"
        ><span style="font-weight:bold;">B</span></button>

        <button tabindex="-1"
            aria-label="Italic"
            v-if="actionName === 'italic'"
            :class="{ 'is-active': editor.isActive('italic') }"
            @click.prevent="editor.chain().focus().toggleItalic().run()"
        ><span style="font-style: italic;">I</span></button>

        <button tabindex="-1"
            aria-label="Strike"
            v-if="actionName === 'strike'"
            :class="{ 'is-active': editor.isActive('strike') }"
            @click.prevent="editor.chain().focus().toggleStrike().run()"
        ><span style="text-decoration: line-through;">S</span></button>

        <button tabindex="-1"
            aria-label="Underline"
            v-if="actionName === 'underline'"
            :class="{ 'is-active': editor.isActive('underline') }"
            @click.prevent="editor.chain().focus().toggleUnderline().run()"
        ><span style="text-decoration: underline;">U</span></button>

        <button tabindex="-1"
            aria-label="Bullet List"
            v-if="actionName === 'bulletList'"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            @click.prevent="editor.chain().focus().toggleBulletList().run()"
        ><i class="ico-list-ul-solid"></i></button>

        <button tabindex="-1"
            aria-label="Numbered List"
            v-if="actionName === 'orderedList'"
            :class="{ 'is-active': editor.isActive('orderedList') }"
            @click.prevent="editor.chain().focus().toggleOrderedList().run()"
        ><i class="ico-list-ol-solid"></i></button>

        <button tabindex="-1"
            aria-label="Undo"
            v-if="actionName === 'undo'"
            @click.prevent="editor.chain().focus().undo().run()"
        >&#x21B6;</button>

        <button tabindex="-1"
            aria-label="Redo"
            v-if="actionName === 'redo'"
            @click.prevent="editor.chain().focus().redo().run()"
        >&#x21B7;</button>
      </span>
    </div>

    <editor-content class="editor-content" :editor="editor" />

    <span class="editor-count">
      <svg width="26" height="26" viewBox="0 0 45 45" style="transform:rotate(-90deg)">
        <circle r="12.5" cx="22.5" cy="22.5" fill="transparent" stroke="#e0e0e0" stroke-width="8"></circle>
        <circle r="12.5" cx="22.5" cy="22.5" stroke-width="8" stroke-linecap="butt" fill="transparent"
                :stroke="strokeColor"
                :stroke-dashoffset="79 - (79 * percentage / 100)"
                stroke-dasharray="78.5px"></circle>
      </svg>
      <span>{{ characterLimit - editor.storage.characterCount.characters() }} tekens over</span>
    </span>
  </div>
</template>
